// eslint-disable-next-line import/no-unresolved -- CRA's import resolver doesn't read the package's `exports` map
import { initObservability, startWebVitals } from "@hyly/observability/react";

import pkg from "../package.json";

// Derive an origin from an env value so HTTP error capture targets ONLY our own
// backends (and excludes third-party / CDN traffic).
const toOrigin = (value) => {
  if (!value) {
    return undefined;
  }

  try {
    const withScheme = /^https?:\/\//.test(value) ? value : `https://${value}`;

    return new URL(withScheme).origin;
  } catch (_) {
    return undefined;
  }
};

/**
 * Initialize Sentry (via @hyly/observability) once, before render.
 *
 * PRODUCTION ONLY: the DSN is defined only in config/security.env.production, so
 * dev/qa/stg have no DSN -> this is a no-op and the app runs exactly as before.
 * (initObservability throws on an empty DSN, so the guard is required.)
 */
export function setupObservability() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;

  if (!dsn) {
    return;
  }

  // Our own backend origins (REST API, CRM, ML chat-adapter) for scoped HTTP capture.
  const apiTargets = [
    toOrigin(process.env.REACT_APP_API_BASE_URL),
    toOrigin(process.env.REACT_APP_CRM_BASE_URL),
    toOrigin(process.env.REACT_APP_WS_BASE_URL),
  ].filter(Boolean);

  initObservability({
    app: "hayley-chatbot",
    dsn,
    environment: process.env.REACT_APP_ENVIRONMENT,
    release: `hayley-chatbot@${pkg.version}`, // from package.json version (matches sentry:sourcemaps; bump the version to distinguish deploys)
    embedded: window.self !== window.top, // mirrors App.js embedded detection
    property: {
      id: new URLSearchParams(window.location.search).get("property_id") || undefined,
    },
    enableTracing: true,
    tracesSampleRate: 0.1, // 10% of sessions traced — perf trends without tracing everyone
    // Session Replay: the package forces masking on (all text/inputs/media masked,
    // non-overridable) so no PII is recorded — recordings show layout, clicks,
    // navigation and timing only. Error-only sampling: buffer the session and flush a
    // replay ONLY when an error is captured (no random-session recording), which keeps
    // volume minimal for this high-traffic embed.
    enableReplay: true,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Capture failed HTTP responses to OUR backends only. 401 is expected
    // (auth/session), so skip it; capture 400 and 402-599.
    enableHttpClientErrors: true,
    httpClientFailedStatusCodes: [
      [400, 400],
      [402, 599],
    ],
    httpClientTargets: apiTargets.length ? apiTargets : undefined,
  });

  startWebVitals();
}
